import React, { useEffect, useState } from "react";
import apiClient from "../../lib/api";
import { useI18n } from "../../i18n";
import { useSettings } from "../../contexts/SettingsContext";
import { toast } from "sonner";
import { Save, MessageCircle, Instagram, Facebook, Linkedin, Twitter, Youtube, ExternalLink } from "lucide-react";

const TikTok = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.21a8.16 8.16 0 0 0 4.77 1.52V6.31a4.85 4.85 0 0 1-1.84-.38v.76z"/>
  </svg>
);

const sanitizePhone = (raw) => (raw || "").replace(/[^\d]/g, "");

const AdminSocial = () => {
  const { lang } = useI18n();
  const { refresh } = useSettings();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiClient.get("/admin/settings").then((r) => setS(r.data?.social || {}));
  }, []);

  if (!s) return <div className="text-ink-muted">Loading…</div>;

  const upd = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...s, whatsapp_number: sanitizePhone(s.whatsapp_number) };
      await apiClient.put("/admin/settings", { social: payload });
      toast.success(lang === "nl" ? "Opgeslagen" : "Saved");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const cleanPhone = sanitizePhone(s.whatsapp_number);
  const waPreview = cleanPhone
    ? `https://wa.me/${cleanPhone}?text=${encodeURIComponent(s.whatsapp_message_nl || "")}`
    : null;

  return (
    <div data-testid="admin-social-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light">{lang === "nl" ? "Social & Contact" : "Social & Contact"}</h1>
          <div className="font-label text-ink-muted mt-1">
            {lang === "nl" ? "WhatsApp knop en social media links voor de hele website" : "WhatsApp button and social media links across the whole site"}
          </div>
        </div>
        <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="social-save">
          <Save className="w-4 h-4" /> {busy ? "…" : (lang === "nl" ? "Opslaan" : "Save")}
        </button>
      </div>

      {/* WhatsApp panel */}
      <div className="border border-black/10 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 bg-[#25D366]/15 text-[#1faa54] rounded-full flex items-center justify-center">
            <MessageCircle className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display text-lg">WhatsApp</div>
            <div className="text-xs text-ink-muted">{lang === "nl" ? "Wordt getoond als zwevende knop linksonder. Laat leeg om te verbergen." : "Shown as a floating button bottom-left. Leave empty to hide."}</div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <F
            label={lang === "nl" ? "Telefoonnummer" : "Phone number"}
            placeholder="+31 6 1234 5678"
            value={s.whatsapp_number}
            onChange={(v) => upd("whatsapp_number", v)}
            testid="social-whatsapp-number"
            hint={lang === "nl" ? "Internationale notatie. Spaties + worden automatisch verwijderd." : "International format. Spaces + are stripped automatically."}
          />
          <F
            label={lang === "nl" ? "Bericht NL" : "Message NL"}
            value={s.whatsapp_message_nl}
            onChange={(v) => upd("whatsapp_message_nl", v)}
            testid="social-whatsapp-message-nl"
            placeholder="Hallo SnapShop, ik heb een vraag."
          />
          <F
            label={lang === "nl" ? "Bericht EN" : "Message EN"}
            value={s.whatsapp_message_en}
            onChange={(v) => upd("whatsapp_message_en", v)}
            testid="social-whatsapp-message-en"
            placeholder="Hello SnapShop, I have a question."
          />
        </div>

        {waPreview && (
          <a href={waPreview} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-4 text-xs text-olive hover:text-ink" data-testid="social-whatsapp-preview">
            <ExternalLink className="w-3.5 h-3.5" /> {lang === "nl" ? "Test link openen" : "Open test link"} → wa.me/{cleanPhone}
          </a>
        )}
      </div>

      {/* Social media panel */}
      <div className="border border-black/10 bg-white p-6 mt-6">
        <div className="font-label text-ink-muted mb-1">{lang === "nl" ? "Social media" : "Social media"}</div>
        <div className="text-xs text-ink-muted mb-5">{lang === "nl" ? "Plak je volledige profiel-URL (https://…). Velden die je leeg laat worden niet getoond in de footer." : "Paste your full profile URL (https://…). Empty fields are hidden from the footer."}</div>
        <div className="grid md:grid-cols-2 gap-3">
          <SocialRow Icon={Instagram} brand="#E4405F" label="Instagram" placeholder="https://instagram.com/snapshop" value={s.instagram_url} onChange={(v) => upd("instagram_url", v)} testid="social-instagram-url" />
          <SocialRow Icon={Facebook} brand="#1877F2" label="Facebook" placeholder="https://facebook.com/snapshop" value={s.facebook_url} onChange={(v) => upd("facebook_url", v)} testid="social-facebook-url" />
          <SocialRow Icon={TikTok} brand="#000000" label="TikTok" placeholder="https://www.tiktok.com/@snapshop" value={s.tiktok_url} onChange={(v) => upd("tiktok_url", v)} testid="social-tiktok-url" />
          <SocialRow Icon={Linkedin} brand="#0A66C2" label="LinkedIn" placeholder="https://linkedin.com/company/snapshop" value={s.linkedin_url} onChange={(v) => upd("linkedin_url", v)} testid="social-linkedin-url" />
          <SocialRow Icon={Twitter} brand="#1DA1F2" label="X / Twitter" placeholder="https://x.com/snapshop" value={s.twitter_url} onChange={(v) => upd("twitter_url", v)} testid="social-twitter-url" />
          <SocialRow Icon={Youtube} brand="#FF0000" label="YouTube" placeholder="https://youtube.com/@snapshop" value={s.youtube_url} onChange={(v) => upd("youtube_url", v)} testid="social-youtube-url" />
        </div>
      </div>
    </div>
  );
};

const F = ({ label, value, onChange, testid, placeholder, hint }) => (
  <label className="block">
    <div className="text-xs font-label text-ink-muted mb-1">{label}</div>
    <input
      data-testid={testid}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="border border-black/15 px-3 py-2 text-sm w-full bg-white"
    />
    {hint && <div className="text-[10px] text-ink-muted mt-1">{hint}</div>}
  </label>
);

const SocialRow = ({ Icon, brand, label, value, onChange, testid, placeholder }) => (
  <label className="flex items-stretch border border-black/10 bg-white hover:border-black/30 transition-colors">
    <div className="w-12 flex items-center justify-center" style={{ color: brand }}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="flex-1 py-2 pr-3 min-w-0">
      <div className="text-[11px] font-label text-ink-muted">{label}</div>
      <input
        data-testid={testid}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-transparent text-sm w-full outline-none font-mono truncate"
      />
    </div>
  </label>
);

export default AdminSocial;
